import * as THREE from "three";
import { config } from "../config.js";
import { mothershipShapes, turretMounts, TURRET_PIVOT_HEIGHT_M, BOW_Z } from "./mothership-geometry.js";

// ポッド(戦闘機)と母艦の見た目。SENSEOFVOID の model-preview/models.js から、形の作り方(半径5mの球+後部の円錐、
// 球に貼る画像の向き、円錐の内側の色)をそのまま持ってきて、敵はまとめ描き(InstancedMesh)で描けるようにした。
// 前方はローカルZマイナス。

const loader = new THREE.TextureLoader();
const skinCache = new Map();
function skinTexture(url) {
  if (!skinCache.has(url)) {
    const tex = loader.load(url);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    tex.offset.x = -0.25; // 画像の真ん中が球の正面(ローカルZマイナス)に来るように(models.js と同じ)
    tex.anisotropy = 4;
    skinCache.set(url, tex);
  }
  return skinCache.get(url);
}

// 底の蓋がない円錐(外側の灰色と内側の色を頂点の色で塗り分ける。models.js の thrusterConeGeometry と同じ形)
const WALL = 0.88;
function thrusterConeGeometry(innerColor) {
  const outer = new THREE.ConeGeometry(1, 2, 12, 1, true);
  const inner = outer.clone().scale(WALL, WALL, WALL);
  const idx = inner.index.array;
  for (let i = 0; i < idx.length; i += 3) [idx[i + 1], idx[i + 2]] = [idx[i + 2], idx[i + 1]];
  const n = inner.attributes.normal.array;
  for (let i = 0; i < n.length; i++) n[i] = -n[i];
  const attrs = { position: [], normal: [], uv: [], color: [] };
  const index = [];
  let base = 0;
  [[outer, new THREE.Color(0x333333)], [inner, new THREE.Color(innerColor)]].forEach(([g, c]) => {
    for (const key of ["position", "normal", "uv"]) attrs[key].push(...g.attributes[key].array);
    for (const i of g.index.array) index.push(i + base);
    for (let i = 0; i < g.attributes.position.count; i++) attrs.color.push(c.r, c.g, c.b);
    base += g.attributes.position.count;
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(attrs.position, 3));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(attrs.normal, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(attrs.uv, 2));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(attrs.color, 3));
  geometry.setIndex(index);
  return geometry;
}

// 円錐を、機体の後ろ(ローカル z=4.5)で後ろ向きに置いた形にする
function placedThruster(innerColor, x) {
  const g = thrusterConeGeometry(innerColor);
  g.rotateX(-Math.PI / 2);
  g.translate(x, 0, 4.5);
  return g;
}

export function createPlayerPod() {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.SphereGeometry(5, 32, 20), new THREE.MeshStandardMaterial({ map: skinTexture(config.graphics.skins.player), metalness: 0.3, roughness: 0.5 }));
  group.add(body);
  const thrusterMat = new THREE.MeshStandardMaterial({ vertexColors: true });
  for (const x of [-2.5, 2.5]) group.add(new THREE.Mesh(placedThruster(config.graphics.thrusterInnerColors.white, x), thrusterMat));
  // 噴射の光(加算で描く。見た目だけ)
  const glowMat = new THREE.SpriteMaterial({ map: glowTexture(), color: 0x8fc8ff, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true });
  group.userData.glows = [-2.5, 2.5].map((x) => {
    const s = new THREE.Sprite(glowMat);
    s.position.set(x, 0, 6.5);
    s.scale.setScalar(5);
    group.add(s);
    return s;
  });
  group.userData.body = body;
  return group;
}

// 敵のポッドのまとめ描き。1つの行列を、球と円錐の2つの InstancedMesh で使う
export function createEnemyPods(max) {
  const bodyMat = new THREE.MeshStandardMaterial({ map: skinTexture(config.graphics.skins.enemy), metalness: 0.3, roughness: 0.55, emissive: new THREE.Color(0xff3300), emissiveIntensity: 0 });
  const danger = addDangerLook(bodyMat);
  const body = new THREE.InstancedMesh(new THREE.SphereGeometry(5, 16, 12), bodyMat, max);
  const thruster = new THREE.InstancedMesh(placedThruster(config.graphics.thrusterInnerColors.red, 0), new THREE.MeshStandardMaterial({ vertexColors: true }), max);
  for (const m of [body, thruster]) {
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    m.frustumCulled = false;
    m.count = 0;
  }
  // 被弾の白い光(色を足す)
  body.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(max * 3).fill(1), 3);
  // seconds: 見た目の時計(秒)。描くたびに index.js が渡す
  return { body, thruster, setTime: (seconds) => (danger.uTime.value = seconds) };
}

// 敵のポッドを「当たると痛そう」に見せる(2026-11-01 オーナー要望「ふわふわ浮いているだけで(特に緑)当たってはいけない印象が弱い。
// スパークを帯びている・熱を持っていそうな軽い描画で」)。
// 敵は最大150機をまとめ描きしているので、粒や光の板を足さず(描く回数が増える)、球の材質のシェーダーに2つを足すだけにした:
//   - 熱: 輪郭(視線に対して横を向いた面)が赤く光り、ゆっくり脈打つ。色の玉の色(緑・赤・青・紫)はそのまま、縁だけ「敵の暖色」
//   - 放電: 表面を這う明るい筋が、ときどきパチッと走る(形は毎回変わる)。自機の色(寒色)と分けるため橙白
// 拾う物(金のダイヤ・緑の十字)は縁の光も放電も持たないので、緑玉と緑の十字が見分けやすくなる。
// 機体ごとの間(まとめ描きの番号)でずらし、全機が揃って光らないようにする。試合の計算には触らない。値は config.graphics.podDanger
// シェーダーの中身: 熱 = 面の向き(normal。視点の空間)とカメラへの向き(vViewPosition)から輪郭ほど強く。
// 放電 = 1秒に sparkRate 回、形と向きを変える。そのうち sparkChance の割合の回だけ、球の一部(毎回ちがう向き)に筋を描く
// (全体が光ると「光る玉」に見えて拾う物と紛れるため)。
// GLSL の中には日本語を書かない(一部のスマホの GPU ドライバーが ASCII 以外の文字でシェーダーの読み込みに失敗するため)
function addDangerLook(material) {
  const D = config.graphics.podDanger;
  const uniforms = {
    uTime: { value: 0 },
    uRimColor: { value: new THREE.Color(D.rimColor) },
    uRim: { value: new THREE.Vector3(D.rimStrength, D.rimPulseHz, D.rimPower) },
    uSparkColor: { value: new THREE.Color(D.sparkColor) },
    uSpark: { value: new THREE.Vector3(D.sparkStrength, D.sparkRate, D.sparkChance) },
  };
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vDangerPos;\nvarying float vDangerSeed;")
      .replace("#include <begin_vertex>", "#include <begin_vertex>\nvDangerPos = position;\nvDangerSeed = fract(float(gl_InstanceID) * 0.618034);");
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        "#include <common>\nvarying vec3 vDangerPos;\nvarying float vDangerSeed;\nuniform float uTime;\nuniform vec3 uRimColor;\nuniform vec3 uRim;\nuniform vec3 uSparkColor;\nuniform vec3 uSpark;"
      )
      .replace(
        "#include <emissivemap_fragment>",
        `#include <emissivemap_fragment>
        {
          float dT = uTime + vDangerSeed * 13.0;
          float facing = clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0);
          float pulse = 0.6 + 0.4 * sin(dT * 6.2831853 * uRim.y);
          totalEmissiveRadiance += uRimColor * pow(1.0 - facing, uRim.z) * pulse * uRim.x;
          float burst = floor(dT * uSpark.y);
          float h = fract(sin(burst * 12.9898 + vDangerSeed * 78.233) * 43758.5453);
          if (h > 1.0 - uSpark.z) {
            vec3 d = normalize(vDangerPos);
            float a = h * 40.0;
            vec3 r = vec3(d.x * cos(a) - d.z * sin(a), d.y, d.x * sin(a) + d.z * cos(a));
            float f = sin(r.x * 4.0 + sin(r.y * 9.0 + h * 20.0) * 1.6) + sin(r.z * 5.0 + sin(r.x * 11.0 - h * 15.0) * 1.4);
            vec3 side = normalize(vec3(sin(a * 1.7), cos(a * 2.3), sin(a * 0.9 + 1.0)));
            float area = smoothstep(-0.3, 0.3, dot(d, side));
            totalEmissiveRadiance += uSparkColor * smoothstep(0.17, 0.0, abs(f)) * area * uSpark.x;
          }
        }`
      );
  };
  return uniforms;
}

// 母艦(ボス)。形は SENSEOFVOID の母艦(主砲の砲口・噴射口・補強の帯・対空砲。mothership-geometry.js)、塗装は SENSEOFVOID の赤軍の母艦の画像。
// SENSEOFVOID の母艦は艦首が −z なので、半回転させて艦首(主砲)を自機の方(+z)へ向ける。大きさは元のまま(長さ200m・横幅80m。当たり判定の半径40mとほぼ同じ)
export function createBossShip() {
  const look = config.graphics.bossLook;
  const group = new THREE.Group();
  const ship = new THREE.Group();
  ship.rotation.y = Math.PI;
  group.add(ship);
  const shapes = mothershipShapes();
  const tex = (url, wrap) => {
    const t = loader.load(url);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 4;
    if (wrap) t.wrapS = THREE.RepeatWrapping;
    return t;
  };
  const surface = { metalness: look.hullMetalness, roughness: look.hullRoughness };
  // 材質の並び: 側面・艦尾・艦首
  const mats = [
    new THREE.MeshStandardMaterial({ map: tex("assets/textures/red_Mother.jpg", true), ...surface }),
    new THREE.MeshStandardMaterial({ map: tex("assets/textures/red_Mother_rear.jpg"), ...surface }),
    new THREE.MeshStandardMaterial({ map: tex("assets/textures/red_Mother_front.jpg"), ...surface }),
  ];
  ship.add(new THREE.Mesh(shapes.hull, mats));
  const partsMat = new THREE.MeshStandardMaterial({ color: look.partsColor, vertexColors: true, metalness: look.partsMetalness, roughness: look.partsRoughness });
  mats.push(partsMat);
  ship.add(new THREE.Mesh(shapes.parts, partsMat));
  ship.add(new THREE.Mesh(shapes.glows, new THREE.MeshBasicMaterial({ color: look.glowColor })));
  // 対空砲の砲塔: 真横を向けて置くだけ(回さない)
  const mounts = turretMounts();
  const heads = new THREE.InstancedMesh(shapes.turretHead, partsMat, mounts.length);
  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  mounts.forEach(({ side, z, x }, i) => {
    q.setFromUnitVectors(up, new THREE.Vector3(side, 0, 0));
    m4.compose(new THREE.Vector3(x + side * TURRET_PIVOT_HEIGHT_M, 0, z), q, new THREE.Vector3(1, 1, 1));
    heads.setMatrixAt(i, m4);
  });
  ship.add(heads);
  // 主砲の砲口の光
  const glowMat = new THREE.SpriteMaterial({ map: glowTexture(), color: 0xff5a2a, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true });
  const muzzle = new THREE.Sprite(glowMat);
  muzzle.position.set(0, 0, -BOW_Z + 16); // 艦首(+z)の砲口の先
  muzzle.scale.setScalar(26);
  group.add(muzzle);
  group.userData.mats = mats;
  return group;
}

// 大玉(SENSEOFVOID の中ボスの大型ポッド)。半径1の球に絵を貼り、正面(絵の真ん中=主砲の砲口)を自機の方(+z)へ向ける。
// 大きさは index.js が 当たり判定の半径 × e.size で掛ける
export function createLargePod() {
  const group = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ map: skinTexture(config.graphics.skins.largePod), metalness: 0.3, roughness: 0.55 });
  const body = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 32), mat);
  body.rotation.y = Math.PI;
  group.add(body);
  const glowMat = new THREE.SpriteMaterial({ map: glowTexture(), color: 0xff8a3a, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true });
  const muzzle = new THREE.Sprite(glowMat);
  muzzle.position.set(0, 0, 1.05);
  muzzle.scale.setScalar(0.5);
  group.add(muzzle);
  group.userData.mats = [mat];
  return group;
}

let glowTex = null;
export function glowTexture() {
  if (glowTex) return glowTex;
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.25, "rgba(255,255,255,0.6)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  glowTex = new THREE.CanvasTexture(c);
  return glowTex;
}
